import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, Users, MapPin, Heart } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * SignupNudgeModal — high-conversion first-visit prompt for anonymous
 * campers. Multiple triggers ensure the modal catches every plausible
 * signup intent without being annoying:
 *   • 8-second delay after page load (previously 12 s — traffic spike
 *     iter181 tightened this since users bounce faster than they scroll)
 *   • Desktop exit-intent — mouse leaves top edge of the viewport
 *   • Mobile bounce catch — page becomes hidden without scrolling
 *   • 2-day cooldown once dismissed (previously 3 days — same reasoning)
 *
 * The Save-heart soft-gate is handled separately by `SignupPromptModal`
 * so we never fire both at once.
 *
 * Silent for signed-in users. Silent inside /admin. Google button lives
 * on /register — the CTA links there so one tap becomes one signup.
 */
const SEEN_KEY = "ccu:signup-nudge:seen";
const COOLDOWN_MS = 2 * 24 * 60 * 60 * 1000; // 2 days
const APPEAR_DELAY_MS = 8000;

export default function SignupNudgeModal() {
  // iter189 — superseded by <GlobalSignupPromptModal> mounted in App.js.
  // Kept as an inert component so existing imports across pages don't
  // break the build. Rendering nothing avoids double-modal stacking.
  return null;
}

// eslint-disable-next-line no-unused-vars
function _LegacySignupNudgeModal() {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) return;
    // Never fire on admin — the operator is signed in but even on the
    // rare unauth path a nudge would be nonsensical.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return;
    let dismissed = false;
    try {
      const raw = localStorage.getItem(SEEN_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (Number.isFinite(ts) && Date.now() - ts < COOLDOWN_MS) dismissed = true;
      }
    } catch { /* private mode — fail open */ }
    if (dismissed) return;

    const openIt = () => setOpen(true);
    const timer = setTimeout(openIt, APPEAR_DELAY_MS);

    // Desktop exit-intent: mouse crossing top boundary usually precedes
    // a tab-close, back-nav, or bookmark bar hunt. Fire immediately.
    const onMouseLeave = (e) => {
      if (e && e.clientY <= 0) openIt();
    };
    document.addEventListener("mouseleave", onMouseLeave);

    // Mobile bounce catch: user switches app or minimises Chrome. If
    // they haven't seen the modal yet, prime it for the return trip.
    const onVisibility = () => {
      if (document.visibilityState === "hidden") openIt();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isAuthenticated]);

  const stamp = () => {
    try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch { /* ignore */ }
  };
  const close = () => { stamp(); setOpen(false); };

  if (!open || isAuthenticated) return null;

  // Preserve current URL so the visitor lands back where they were.
  const next = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/browse";
  const registerHref = next && next !== "/" && next !== "/register"
    ? `/register?next=${encodeURIComponent(next)}`
    : "/register";

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-nudge-title"
      onClick={close}
      data-testid="signup-nudge-modal"
    >
      <div
        className="relative bg-white w-full max-w-md sm:max-w-lg border-2 border-[#0e0e0e] shadow-[0_8px_0_0_#0e0e0e] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute top-2 right-2 z-10 text-[#666] hover:text-[#111] p-1.5"
          data-testid="signup-nudge-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="bg-gradient-to-br from-[#ff4500] via-[#ff6a2b] to-[#ffa66c] text-white px-5 pt-5 pb-6 sm:px-7 sm:pt-6 sm:pb-8">
          <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2 py-0.5 text-[10px] mono uppercase tracking-widest font-black mb-3">
            <Sparkles className="w-3 h-3" /> Free · 60-second signup
          </div>
          <h2
            id="signup-nudge-title"
            className="font-display text-2xl sm:text-3xl leading-tight mb-2"
            data-testid="signup-nudge-title"
          >
            Join the campfire — it&rsquo;s free while we grow
          </h2>
          <p className="text-white/95 text-sm sm:text-base leading-snug">
            22,000+ UK campsites (300 rotate free every Monday · sign up to unlock the rest), real camper reviews, weather alerts, and
            a growing community of British campers swapping tips. One
            minute to sign up — no card, no catches.
          </p>
        </div>

        <div className="px-5 sm:px-7 py-4 sm:py-5 space-y-2.5">
          <Perk icon={MapPin}    text="Save unlimited favourite campsites" />
          <Perk icon={Users}     text="Share trips + chat with fellow campers" />
          <Perk icon={Heart}     text="Get weather + price alerts on saved sites" />
        </div>

        <div className="px-5 sm:px-7 pb-5 sm:pb-6 pt-1 flex flex-col gap-2">
          <Link
            to={registerHref}
            onClick={stamp}
            className="w-full inline-flex items-center justify-center bg-[#0a7f3f] text-white font-black text-sm uppercase tracking-widest px-4 py-3 shadow-[0_4px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#111] transition-transform"
            data-testid="signup-nudge-cta"
          >
            Create free account
          </Link>
          <button
            type="button"
            onClick={close}
            className="text-[11px] text-[#666] hover:text-[#111] uppercase tracking-widest font-semibold py-1"
            data-testid="signup-nudge-later"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

function Perk({ icon: Icon, text }) {
  return (
    <div className="flex items-start gap-2.5 text-sm text-[#333]">
      <div className="w-7 h-7 shrink-0 bg-[#fff4ec] text-[#ff4500] flex items-center justify-center">
        <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
      </div>
      <span className="pt-1">{text}</span>
    </div>
  );
}
